import { useEffect, useState } from "react";
import { ChevronDown, ChevronRight, Eye, Loader2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/lib/constants";
import ReferenceSetPicker from "./ReferenceSetPicker";

/**
 * Phase 5 Day 6 + Day 9 — CriticPanel with live Run Critic + cached-
 * on-load surfacing with render_hash staleness handling.
 *
 * Spec refs: § 3.1 + Remi's Day 9 UX-correction spec.
 *
 * Three load states:
 *   1. No cached data → empty panel + Run Critic button (Day 6).
 *   2. Cached data, render_hash matches current → read-only verdict
 *      badges with "Last assessed: <timestamp>". Acknowledge buttons
 *      active.
 *   3. Cached data, render_hash MISMATCHES current → "Stale: re-render
 *      invalidated last verdict" banner. Old verdict NOT shown.
 */

const DIMENSIONS = [
  { key: "focal_point", label: "Focal point" },
  { key: "hierarchy", label: "Hierarchy" },
  { key: "brand_presence", label: "Brand presence" },
  { key: "repetition", label: "Repetition" },
];

function StatusBadge({ result, dim, testid, inPreCalibration }) {
  // Pre-calibration framing wins over result status — operator hasn't
  // locked thresholds yet, so every assessment is informational.
  if (inPreCalibration || !result) {
    return (
      <span data-testid={testid} className="inline-flex items-center rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-medium text-slate-600">
        ⓘ data
      </span>
    );
  }
  if (result.state === "needs_more_history") {
    return <span data-testid={testid} className="inline-flex items-center rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-medium text-amber-700">⏸ needs more history</span>;
  }
  if (result.state === "no_reference_set") {
    return <span data-testid={testid} className="inline-flex items-center rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-medium text-slate-600">no reference set</span>;
  }
  if (result.state === "budget_exhausted") {
    return <span data-testid={testid} className="inline-flex items-center rounded-full bg-red-50 px-2 py-0.5 text-[10px] font-medium text-red-700">budget exhausted</span>;
  }
  if (result.state === "vlm_failure") {
    return <span data-testid={testid} className="inline-flex items-center rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-medium text-amber-700">vlm failure</span>;
  }
  const env = result.envelope;
  if (env?.matches_intent === true) {
    return <span data-testid={testid} className="inline-flex items-center rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-medium text-emerald-700">✓ landed</span>;
  }
  if (env?.matches_intent === false) {
    return <span data-testid={testid} className="inline-flex items-center rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-medium text-amber-700">⚠ flagged</span>;
  }
  return <span data-testid={testid} className="inline-flex items-center rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-medium text-slate-600">ⓘ data</span>;
}

function DimensionRow({ dim, result, inPreCalibration, onAcknowledge, ackInFlight }) {
  const statusTestId = `critic-dimension-status-${dim.key}`;
  const env = result?.envelope;
  let detail = null;
  if (env) {
    if (dim.key === "focal_point" && env.iou !== undefined) {
      detail = <span>IoU {Number(env.iou).toFixed(3)}</span>;
    } else if (dim.key === "hierarchy" && env.edit_distance !== undefined) {
      detail = (
        <span>
          dist {env.edit_distance} · {(env.perceived_order || []).join(" → ") || "—"}
        </span>
      );
    } else if (dim.key === "brand_presence" && env.colour_presence !== undefined) {
      detail = (
        <span>
          colour {env.colour_presence}/5 · logo {env.logo_placement}/5 · structural {env.structural_similarity}/5
        </span>
      );
    } else if (dim.key === "repetition") {
      if (env.max_similarity_score !== undefined && env.max_similarity_score !== null) {
        detail = (
          <span>
            similarity {Number(env.max_similarity_score).toFixed(2)} · compared {(env.compared_against_card_ids || []).length}
          </span>
        );
      } else if (env.state) {
        detail = <span>state: {env.state} · history {env.history_window_n}/{env.history_window_n_required}</span>;
      }
    }
  }
  const showAcknowledge = !inPreCalibration && env?.matches_intent === false && !result?.is_stale && onAcknowledge;
  return (
    <div data-testid={`critic-dimension-${dim.key.replace(/_/g, "-")}`} className="rounded border border-slate-200 px-3 py-2">
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold text-slate-800">{dim.label.toUpperCase()}</span>
        <StatusBadge result={result} dim={dim.key} testid={statusTestId} inPreCalibration={inPreCalibration} />
      </div>
      {detail && <div className="mt-1 text-[11px] text-slate-600 tabular-nums">{detail}</div>}
      {env?.rationale && (
        <p data-testid={`critic-observation-${dim.key.replace(/_/g, "-")}`} className="mt-2 text-xs text-slate-600 leading-relaxed">
          {env.rationale}
        </p>
      )}
      {result?.cache_hit && result?.assessed_at && (
        <div className="mt-1 text-[10px] text-slate-400 italic">
          Last assessed: {new Date(result.assessed_at).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })}
          {result.cost_usd ? ` · $${Number(result.cost_usd).toFixed(4)}` : ""}
        </div>
      )}
      {result?.state === "no_reference_set" && (
        <p className="mt-2 text-[11px] text-slate-500">Add reference cards via the picker below to enable brand-presence assessment.</p>
      )}
      {result?.state === "needs_more_history" && (
        <p className="mt-2 text-[11px] text-slate-500">Repetition dimension ships dormant — calibrates after ~4 weeks of publish history.</p>
      )}
      {result?.state === "acknowledged_recently" && (
        <p className="mt-2 text-[11px] text-slate-500">Repetition flag was acknowledged recently — re-fire suppressed within the workspace window.</p>
      )}
      {showAcknowledge && (
        <div className="mt-2">
          <button
            type="button"
            onClick={() => onAcknowledge(dim.key)}
            disabled={ackInFlight === dim.key}
            data-testid={`critic-acknowledge-${dim.key.replace(/_/g, "-")}`}
            className="rounded border border-slate-300 bg-white px-2 py-1 text-[11px] font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
          >
            Acknowledge & ship anyway
          </button>
        </div>
      )}
    </div>
  );
}

export default function CriticPanel({ card, workspaceId, currentUser }) {
  const [open, setOpen] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [running, setRunning] = useState(false);
  const [assessment, setAssessment] = useState(null);
  const [cachedLoadDone, setCachedLoadDone] = useState(false);
  const [stale, setStale] = useState(false);
  const [ackInFlight, setAckInFlight] = useState(null);

  // Day 9: fetch the cached verdict on mount so the operator sees
  // previous critic reads instantly without clicking Run Critic.
  useEffect(() => {
    let cancelled = false;
    const fetchCached = async () => {
      if (!card?.card_id || !workspaceId) return;
      try {
        const r = await fetch(`${API}/v2/workspaces/${workspaceId}/critic/assessments/${card.card_id}/latest`, { credentials: "include" });
        if (!r.ok) return;
        const data = await r.json();
        if (cancelled) return;
        if (data.has_cached_data) {
          if (data.is_stale) {
            // Mismatch: don't surface stale verdicts as if current.
            setStale(true);
            setAssessment(null);
          } else {
            setStale(false);
            setAssessment({
              dimensions: data.dimensions,
              render_hash: data.current_render_hash,
              cached_load: true,
            });
          }
        }
      } catch (e) {
        // Cache fetch failure is non-fatal — fall back to Day 6 empty
        // panel; operator can still click Run Critic.
        console.warn("cached assessment fetch failed", e);
      } finally {
        if (!cancelled) setCachedLoadDone(true);
      }
    };
    fetchCached();
    return () => { cancelled = true; };
  }, [card?.card_id, workspaceId]);

  // Pre-calibration framing applies when no thresholds are locked
  // AND no assessment has rendered. After Run Critic returns, panel
  // moves to production framing.
  const inPreCalibration = !assessment;
  const dimensions = assessment?.dimensions || {};

  const runCritic = async () => {
    if (!card?.card_id || !workspaceId) return;
    setRunning(true);
    try {
      const r = await fetch(`${API}/v2/workspaces/${workspaceId}/critic/assess/${card.card_id}`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({}),
      });
      if (!r.ok) throw new Error(`status ${r.status}`);
      const data = await r.json();
      setAssessment(data);
      setStale(false);
      if (data.state === "no_render_yet") {
        toast.warning("This card has no rendered image yet. Render it via the editorial compositor first.");
      } else if (data.state === "card_not_found") {
        toast.error("Card not found in workspace.");
      } else {
        toast.success("Critic assessment complete");
      }
    } catch (e) {
      console.error("Run Critic failed", e);
      toast.error("Run Critic failed");
    } finally {
      setRunning(false);
    }
  };

  const onAcknowledge = async (dimension) => {
    if (!card?.card_id || !workspaceId) return;
    const renderHash = assessment?.render_hash || dimensions?.[dimension]?.render_hash;
    if (!renderHash) {
      toast.error("No render hash available — re-run critic first.");
      return;
    }
    setAckInFlight(dimension);
    try {
      const r = await fetch(`${API}/v2/workspaces/${workspaceId}/critic/acknowledge`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          card_id: card.card_id,
          dimension,
          render_hash: renderHash,
          actor_email: currentUser?.email,
        }),
      });
      if (!r.ok) throw new Error(`status ${r.status}`);
      toast.success(`${dimension} acknowledged. Will ship despite the flag.`);
    } catch (e) {
      console.error("Acknowledge failed", e);
      toast.error("Acknowledge failed");
    } finally {
      setAckInFlight(null);
    }
  };

  return (
    <div className="rounded-lg border border-slate-200 bg-white" data-testid="critic-panel">
      <button type="button" onClick={() => setOpen((v) => !v)} data-testid="critic-panel-toggle" className="w-full flex items-center justify-between px-4 py-3 text-left">
        <div className="flex items-center gap-2">
          <Eye className="h-4 w-4 text-slate-500" />
          <span className="text-sm font-semibold text-slate-800">VLM Critic</span>
          {inPreCalibration && !stale && (
            <span data-testid="critic-panel-precalibration-badge" className="ml-2 inline-flex items-center rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-medium text-slate-600">
              Pre-calibration
            </span>
          )}
          {stale && (
            <span data-testid="critic-panel-stale-badge" className="ml-2 inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-medium text-amber-700">
              <AlertTriangle className="h-3 w-3" /> Stale
            </span>
          )}
          {assessment?.cached_load && !stale && (
            <span data-testid="critic-panel-cached-badge" className="ml-2 inline-flex items-center rounded-full bg-blue-50 px-2 py-0.5 text-[10px] font-medium text-blue-700">
              cached read
            </span>
          )}
          {assessment && !assessment.cached_load && (
            <span className="ml-2 text-[10px] text-slate-500 tabular-nums">
              {assessment.budget_state && `budget: ${assessment.budget_state}`}
            </span>
          )}
        </div>
        {open ? <ChevronDown className="h-4 w-4 text-slate-500" /> : <ChevronRight className="h-4 w-4 text-slate-500" />}
      </button>

      {open && (
        <div className="border-t border-slate-200 px-4 py-3 space-y-3">
          {!cachedLoadDone && (
            <div className="rounded border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600 flex items-center gap-2" data-testid="critic-panel-loading">
              <Loader2 className="h-3 w-3 animate-spin" /> Loading cached verdict…
            </div>
          )}

          {stale && (
            <div data-testid="critic-panel-stale-banner" className="rounded border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900">
              <span className="font-semibold">Stale: </span>
              the render has changed since the last assessment. Click <span className="font-semibold">Run Critic</span> to refresh — previous verdicts apply to an older version of this card.
            </div>
          )}

          {inPreCalibration && !stale && cachedLoadDone && (
            <div data-testid="critic-panel-precalibration-banner" className="rounded border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-700">
              Pre-calibration data collection — your labels in Days 3-5 will set the thresholds.
            </div>
          )}

          {assessment?.state === "no_render_yet" && (
            <div className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
              No rendered image available for this card. The critic needs the published PNG to assess.
            </div>
          )}

          {DIMENSIONS.map((dim) => (
            <DimensionRow
              key={dim.key}
              dim={dim}
              result={dimensions[dim.key]}
              inPreCalibration={inPreCalibration}
              onAcknowledge={onAcknowledge}
              ackInFlight={ackInFlight}
            />
          ))}

          <div className="pt-1 flex items-center gap-2 flex-wrap">
            <button
              type="button"
              onClick={runCritic}
              disabled={running || !card?.card_id}
              data-testid="critic-run-now-btn"
              className="rounded border border-blue-400 bg-blue-50 px-3 py-1.5 text-xs font-medium text-blue-900 hover:bg-blue-100 disabled:opacity-50 flex items-center gap-1"
            >
              {running ? <Loader2 className="h-3 w-3 animate-spin" /> : null}
              {running ? "Running…" : (stale ? "Run Critic (refresh)" : "Run Critic")}
            </button>
            <button
              type="button"
              onClick={() => setPickerOpen(true)}
              data-testid="critic-open-reference-picker-btn"
              className="rounded border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
            >
              Reference set…
            </button>
            {assessment?.render_hash && (
              <span className="text-[10px] text-slate-400 font-mono">
                render: {assessment.render_hash.slice(0, 8)}…
              </span>
            )}
          </div>
        </div>
      )}

      <ReferenceSetPicker
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        workspaceId={workspaceId}
      />
    </div>
  );
}
